import React, { useState, useEffect } from 'react';

const PENDING_STORAGE_KEY = 'pending_contract_data';

/**
 * Возвращает непросроченный черновик договора из localStorage (как при восстановлении в ЛК) или null.
 */
export function getFreshPendingContractData() {
    try {
        const raw = localStorage.getItem(PENDING_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const hoursSinceCreation = (Date.now() - (parsed.timestamp || 0)) / (1000 * 60 * 60);
        if (hoursSinceCreation >= 24) {
            localStorage.removeItem(PENDING_STORAGE_KEY);
            return null;
        }
        if (!parsed.data || typeof parsed.data !== 'object' || !parsed.templateId) return null;
        return parsed;
    } catch (error) {
        console.error('Ошибка чтения временных данных договора:', error);
        try {
            localStorage.removeItem(PENDING_STORAGE_KEY);
        } catch {
            /* ignore */
        }
        return null;
    }
}

/**
 * Баннер на страницах входа/регистрации: пользователь пришёл из конструктора с сохранённым прогрессом.
 */
export const PendingDraftReassuranceBanner = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(!!getFreshPendingContractData());
    }, []);

    if (!visible) return null;

    return (
        <div className="auth-pending-draft-banner" role="status" aria-live="polite">
            <div className="auth-pending-draft-banner-icon" aria-hidden>✓</div>
            <div className="auth-pending-draft-banner-body">
                <strong className="auth-pending-draft-banner-title">Один шаг до договора</strong>
                <p className="auth-pending-draft-banner-text">
                    Ваши данные сохранены. Войдите или создайте аккаунт, чтобы скачать договор — заполнять заново не придётся.
                </p>
            </div>
        </div>
    );
};

/**
 * Компонент модального окна, предлагающего авторизацию для неавторизованных пользователей.
 * @param {object} contractData - Текущие данные договора для сохранения.
 * @param {string} trigger - Причина вызова окна ('download', 'save', 'access').
 * @param {function} onClose - Функция для закрытия модального окна.
 * @param {boolean} showModal - Флаг для управления видимостью.
 */
const AuthPrompt = ({ contractData = null, trigger = 'download', onClose = null, showModal = true }) => {
    const [isVisible, setIsVisible] = useState(showModal);

    // --- ОБРАБОТЧИКИ ---

    const handleClose = () => {
        setIsVisible(false);
        if (onClose) {
            onClose();
        }
    };

    const savePendingData = () => {
        if (contractData) {
            const pendingData = { 
                data: contractData, 
                timestamp: Date.now(), 
                trigger: trigger,
                // Важно: предполагаем, что contractData содержит templateId
                templateId: contractData.templateId || 'rent_apartment' 
            };
            localStorage.setItem('pending_contract_data', JSON.stringify(pendingData));
        }
    };

    const handleRegister = () => {
        savePendingData();
        window.location.href = '/register';
    };

    const handleLogin = () => {
        savePendingData();
        window.location.href = '/login';
    };

    // --- КОНТЕНТ ---

    const getPromptContent = () => {
        switch (trigger) {
            case 'download':
                return { title: 'Войдите для скачивания', subtitle: 'Чтобы скачать договор в Word или PDF, войдите или зарегистрируйтесь — заполненная форма подтянется автоматически', icon: '📄' };
            case 'save':
                return { title: 'Сохраните ваш прогресс', subtitle: 'Войдите в систему, чтобы сохранить договор и не потерять ваши данные', icon: '💾' };
            case 'access':
                return { title: 'Доступ ограничен', subtitle: 'Для доступа к этой функции необходимо войти в систему', icon: '🔒' };
            default:
                return { title: 'Требуется авторизация', subtitle: 'Войдите в систему для продолжения работы', icon: '👤' };
        }
    };

    const content = getPromptContent();

    // --- РЕНДЕР ---

    if (!isVisible) {
        return null;
    }

    return (
        <div className="auth-prompt-overlay">
            <div className="auth-prompt-modal">
                {onClose && (
                    <button className="auth-prompt-close" onClick={handleClose} aria-label="Закрыть">×</button>
                )}
                <div className="auth-prompt-content">
                    <div className="auth-prompt-header">
                        <div className="auth-prompt-icon">{content.icon}</div>
                        <h3 className="auth-prompt-title">{content.title}</h3>
                        <p className="auth-prompt-subtitle">{content.subtitle}</p>
                    </div>
                    <div className="data-safety-notice data-safety-notice--prominent">
                        <div className="safety-icon" aria-hidden>✓</div>
                        <div className="safety-text">
                            <strong>Прогресс не потеряется</strong>
                            <p>Текущая версия договора сохраняется в браузере и переносится в личный кабинет после регистрации или входа.</p>
                        </div>
                    </div>
                    <div className="auth-prompt-actions">
                        <button className="auth-prompt-btn auth-prompt-btn-primary" onClick={handleRegister}>Создать аккаунт</button>
                        <button className="auth-prompt-btn auth-prompt-btn-secondary" onClick={handleLogin}>Войти в существующий</button>
                    </div>
                    <div className="auth-prompt-footer">
                        <p className="auth-prompt-note">🚀 Регистрация займет меньше минуты</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Компактная версия AuthPrompt для встраивания в компоненты.
 */
export const AuthPromptInline = ({ contractData = null, trigger = 'download', message = 'Войдите в систему для продолжения' }) => {
    const savePendingData = () => {
        if (contractData) {
            const pendingData = { 
                data: contractData, 
                timestamp: Date.now(), 
                trigger: trigger,
                templateId: contractData.templateId || 'rent_apartment'
            };
            localStorage.setItem('pending_contract_data', JSON.stringify(pendingData));
        }
    };

    const handleRegister = () => {
        savePendingData();
        window.location.href = '/register';
    };

    const handleLogin = () => {
        savePendingData();
        window.location.href = '/login';
    };

    return (
        <div className="auth-prompt-inline">
            <div className="auth-prompt-inline-content">
                <div className="auth-prompt-inline-icon">🔒</div>
                <div className="auth-prompt-inline-text">
                    <p className="auth-prompt-inline-message">{message}</p>
                </div>
            </div>
            <div className="auth-prompt-inline-actions">
                <button className="auth-prompt-inline-btn auth-prompt-inline-btn-primary" onClick={handleRegister}>Регистрация</button>
                <button className="auth-prompt-inline-btn auth-prompt-inline-btn-secondary" onClick={handleLogin}>Вход</button>
            </div>
        </div>
    );
};

/**
 * ✨ ИСПРАВЛЕННЫЙ ХУК для работы с сохраненными данными после авторизации.
 * Разделяет логику чтения и очистки данных для предотвращения "гонки состояний".
 */
export const usePendingContractData = () => {
    /**
     * Читает данные из localStorage, но НЕ удаляет их.
     * @returns {object|null} - Распарсенные данные или null.
     */
    const restoreData = () => getFreshPendingContractData();

    /**
     * Удаляет временные данные из localStorage.
     * Эту функцию следует вызывать ПОСЛЕ успешного использования восстановленных данных.
     */
    const clearPendingData = () => {
        try {
            localStorage.removeItem(PENDING_STORAGE_KEY);
        } catch (error) {
            console.error('Ошибка очистки временных данных:', error);
        }
    };

    return { restoreData, clearPendingData };
};

export default AuthPrompt;